import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  Colors,
  CustomUserStyle,
  UserCustomConfig
} from '../models/user-custom-config.model';
import { from, map, catchError, of, take, tap } from 'rxjs';
import { Router } from '@angular/router';
import { palette, updatePreset } from '@primeuix/themes';
import { Preset } from '@primeuix/themes/types';
import { AuthStore } from '../store/auth/auth.store';

@Injectable({
  providedIn: 'root'
})
export class UserConfigService {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);

  getUserConfig(tenantId: string) {
    const config = environment.USER_CONFIG.replace('{tenantId}', tenantId);
    return from(
      fetch(config).then((response): Promise<UserCustomConfig> => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        // The tenant config is static JSON published per tenant; its shape is
        // trusted rather than validated here.
        return response.json() as Promise<UserCustomConfig>;
      })
    );
  }

  setTheme(tenantId: string) {
    return this.getUserConfig(tenantId).pipe(
      take(1),
      map((config) => ({
        colors: config.style.colors,
        headerLogo: config.headerLogo
      })),
      tap((style: CustomUserStyle) => {
        this.authStore.setTheme(style);

        const primaryShades = palette(
          style.colors.primaryColor ?? ''
        ) as Record<string, string>;
        const secondaryShades = palette(
          style.colors.secondaryColor ?? ''
        ) as Record<string, string>;

        // Every Colors value is a string; Object.entries cannot infer that
        // from an interface.
        const colors = Object.entries(style.colors) as [string, string][];
        for (const [key, value] of colors) {
          this.setColor(this.formatToCssVar(key), value);
        }

        this.updatePrimeNGTheme(primaryShades, secondaryShades);

        if (style.headerLogo) this.authStore.setTenantLogo(style.headerLogo);
      }),
      catchError((error: unknown) => {
        console.error('Error fetching user config:', error);
        this.authStore.setTheme(null);
        this.router.navigate(['error'], {
          queryParams: { detail: 'No theme found.' }
        });
        // Still emit a style with colors, so loadMemberships runs.
        const fallbackStyle: { colors: Partial<Colors> } = { colors: {} };
        return of(fallbackStyle);
      })
    );
  }

  setColor(property: string, value: string) {
    document.documentElement.style.setProperty(property, value);
  }

  formatToCssVar(str: string): string {
    return `--${str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`;
  }

  private updatePrimeNGTheme(
    primaryShades: Record<string, string>,
    secondaryShades: Record<string, string>
  ): void {
    const customPreset = this.createCustomPreset(
      primaryShades,
      secondaryShades
    );
    updatePreset(customPreset);
  }

  private createCustomPreset(
    primaryShades: Record<string, string>,
    secondaryShades: Record<string, string>
  ): Preset {
    return {
      semantic: {
        primary: primaryShades,
        secondary: secondaryShades,
        colorScheme: {
          light: {
            primary: {
              contrastColor: 'var(--primary-contrast-color)',
              hoverColor: 'var(--secondary-color)',
              activeColor: 'var(--secondary-color)',
              textColor: 'var(--primary-text-color)'
            }
          }
        }
      },
      components: {
        button: {
          root: {
            paddingX: '1.5rem',
            paddingY: '0.5rem',
            iconOnlyWidth: '2rem',
            focusRing: {
              width: '0'
            }
          },
          colorScheme: {
            light: {
              root: {
                primary: {
                  color: 'var(--primary-btn-text-color)',
                  activeColor: 'var(--primary-btn-text-color)',
                  hoverColor: 'var(--primary-btn-text-color)'
                }
              },
              outlined: {
                primary: {
                  borderColor: 'var(--primary-color)'
                }
              },
              text: {
                primary: {
                  hoverBackground: 'transparent',
                  activeBackground: 'transparent'
                }
              }
            }
          }
        },
        checkbox: {
          icon: {
            checkedColor: 'white',
            checkedHoverColor: 'white'
          }
        },
        chip: {
          colorScheme: {
            light: {
              root: {
                background: '#dee2e6'
              }
            }
          }
        },
        datatable: {
          colorScheme: {
            light: {
              headerCell: {
                background: 'var(--table-primary-background)',
                color: 'var(--table-header-text)'
              }
            }
          }
        },
        menu: {
          submenuLabel: {
            color: 'inherit',
            fontWeight: '700'
          }
        },
        panel: {
          content: {
            padding: '0'
          }
        },
        radiobutton: {
          root: {
            checkedBackground: '#fff',
            checkedBorderColor: 'var(--primary-color)',
            checkedHoverBackground: '#fff',
            checkedHoverBorderColor: 'var(--primary-color)'
          },
          icon: {
            checkedColor: 'var(--primary-color)',
            checkedHoverColor: 'var(--primary-color)'
          }
        },
        steps: {
          itemNumber: {
            activeBackground: 'var(--primary-color)',
            activeColor: 'white',
            background: '#e0e0e0',
            color: 'white',
            shadow: 'none'
          },
          itemLabel: {
            color: '#9e9e9e'
          }
        },
        toast: {
          text: {
            gap: '0rem'
          }
        }
      }
    };
  }
}
